import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CreateDisease } from 'features/disease';
import { EditVaccine } from 'features/vaccine';

import { countryModel } from 'entities/country';
import { DiseaseTablePaginated, EditDiseaseDialog, diseaseModel } from 'entities/disease';
import { userModel } from 'entities/user';
import { vaccineModel } from 'entities/vaccine';

import { useDebouncedSearch } from 'shared/lib';
import { LoadingProgress } from 'shared/ui';

export const DiseaseTab = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Получаем параметры из URL или используем значения по умолчанию
  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '20', 10));

  // Состояние для редактирования
  const [editDiseaseId, setEditDiseaseId] = useState<string | null>(null);
  const [editPreselectedCountryCode, setEditPreselectedCountryCode] = useState<string>('');
  const [editVaccineId, setEditVaccineId] = useState<string | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);
  const [loadingVaccineId, setLoadingVaccineId] = useState<string | null>(null);

  const { data: userData } = userModel.useUser();
  const { data: countriesData } = countryModel.useAllCountries();

  // Мутации
  const {
    mutate: createDisease,
    isSuccess: isCreateSuccess,
    isLoading: isCreating,
  } = diseaseModel.postNewDiseaseAsync();
  const {
    mutate: updateDisease,
    isSuccess: isUpdateSuccess,
    isLoading: isUpdating,
  } = diseaseModel.updateDiseaseAsync();
  const {
    mutate: updateVaccine,
    isSuccess: isVaccineUpdateSuccess,
    isLoading: isVaccineUpdating,
  } = vaccineModel.updateVaccineAsync();

  // Используем debounce для фильтра чтобы не запускать запрос при каждом символе
  const debouncedFilter = useDebouncedSearch(filter, 400);

  // Создаем объект параметров для API с использованием useMemo
  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
    }),
    [debouncedFilter, sortBy, sortOrder, page, rowsPerPage],
  );

  const { isLoading, isFetching } = diseaseModel.getDiseasesAllAsync(params)(dispatch);
  const { diseaseList, pagination } = diseaseModel.useDiseaseList();

  // Обновляем URL при изменении параметров (используем debouncedFilter для URL)
  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, setSearchParams]);

  // Закрываем диалоги при успешном обновлении
  useEffect(() => {
    if (isUpdateSuccess) {
      setEditDiseaseId(null);
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isVaccineUpdateSuccess) {
      setEditVaccineId(null);
      // Обновляем таблицу болезней, так как вакцина могла измениться
      void queryClient.invalidateQueries([diseaseModel.DISEASES_ALL_QUERY_KEY]);
    }
  }, [isVaccineUpdateSuccess, queryClient]);

  // Мемоизируем обработчики с помощью useCallback
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    setPage(1);
  }, []);

  const handleClearFilter = useCallback(() => {
    setFilter('');
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handleEditDisease = useCallback((diseaseId: string) => {
    setLoadingEditId(diseaseId);
    setEditPreselectedCountryCode('');
    setEditDiseaseId(diseaseId);
  }, []);

  const handleAlertClick = useCallback((diseaseId: string, countryCode: string) => {
    setLoadingEditId(diseaseId);
    setEditPreselectedCountryCode(countryCode);
    setEditDiseaseId(diseaseId);
  }, []);

  const handleDataLoaded = useCallback(() => {
    setLoadingEditId(null);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditDiseaseId(null);
    setEditPreselectedCountryCode('');
    setLoadingEditId(null);
  }, []);

  const handleVaccineClick = useCallback((vaccineId: string) => {
    setLoadingVaccineId(vaccineId);
    setEditVaccineId(vaccineId);
  }, []);

  const handleVaccineDataLoaded = useCallback(() => {
    setLoadingVaccineId(null);
  }, []);

  const handleCloseVaccineDialog = useCallback(() => {
    setEditVaccineId(null);
    setLoadingVaccineId(null);
  }, []);

  // Показываем LoadingProgress только при первой загрузке (когда еще нет данных)
  if (isLoading && !diseaseList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column">
      <Paper>
        <Box display="flex" alignItems="center" p="8px 16px">
          <Typography variant="h6">Human Disease control panel</Typography>
          <Box display="flex" alignItems="center" ml={3} mr="auto">
            <TextField
              sx={{ minWidth: '350px', width: '100%' }}
              placeholder="Enter name or code"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton onClick={handleClearFilter}>
                    <CloseIcon />
                  </IconButton>
                ),
              }}
              size="small"
              value={filter}
              onChange={handleFilterChange}
            />
            <Typography px={2} width="100%">
              {pagination?.total ?? 0} diseases
            </Typography>
          </Box>
          <CreateDisease
            onSubmit={createDisease}
            isSuccess={isCreateSuccess}
            isLoading={isCreating}
            diseaseList={diseaseList}
          />
        </Box>
      </Paper>
      <Box mt={1}>
        <DiseaseTablePaginated
          list={diseaseList}
          pagination={pagination}
          page={page}
          rowsPerPage={rowsPerPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          isLoading={isFetching}
          loadingEditId={loadingEditId}
          loadingVaccineId={loadingVaccineId}
          countriesList={countriesData?.data ?? []}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          onEdit={handleEditDisease}
          onVaccineClick={handleVaccineClick}
          onAlertClick={handleAlertClick}
        />
      </Box>

      {/* Edit Disease Dialog */}
      {editDiseaseId && (
        <EditDiseaseDialog
          diseaseId={editDiseaseId}
          userRole={userData?.role}
          isLoading={isUpdating}
          preselectedCountryCode={editPreselectedCountryCode}
          onClose={handleCloseEdit}
          onVerified={updateDisease}
          onSubmit={updateDisease}
          onDataLoaded={handleDataLoaded}
        />
      )}

      {/* Edit Vaccine Dialog */}
      {editVaccineId && (
        <EditVaccine
          vaccineId={editVaccineId}
          isOpen={true}
          onClose={handleCloseVaccineDialog}
          updateFn={updateVaccine}
          isLoadingUpdate={isVaccineUpdating}
          isSuccessUpdate={isVaccineUpdateSuccess}
          onDataLoaded={handleVaccineDataLoaded}
        />
      )}
    </Box>
  );
};
